import { useTranslation } from 'react-i18next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
} from '@c2fo/liquidity';
import { EllipsisVerticalIcon } from '@c2fo/liquidity/icons';
import emptystatements from '@/enterprise/assets/emptystatementsgrey.svg?url';
import invoices from '@/enterprise/assets/invoicesgrey.svg?url';
import { useBuyerNavigationLayoutContext } from '@/enterprise/components/BuyerNavigationLayout';
import NoDataState from '@/enterprise/components/NoDataState';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  TableSortArrow,
  TableDisplay,
  TableDisplaySkeletonRows,
  TableSortDirection,
} from '@/enterprise/components/Table';
import useIneligibleInvoices, {
  IneligibleInvoiceFetchOptions,
  IneligibleInvoiceSort,
} from '@/enterprise/data/useIneligibleInvoices';
import useIneligibleInvoiceStats from '@/enterprise/data/useIneligibleInvoiceStats';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import getInvoiceDisplay from '@/utils/getInvoiceDisplay';
import useInvoiceDownloadUrl from '@/utils/useInvoiceDownloadUrl';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useUserHasIndiaDivisions from '@/utils/useUserHasIndiaDivisions';

type IneligibleInvoiceSortKey = Exclude<IneligibleInvoiceSort, `-${string}`>;

interface IneligibleInvoiceTableSort {
  key: IneligibleInvoiceSortKey;
  direction: TableSortDirection;
}

const IneligibleInvoiceTable = ({
  clearFilter,
  handleSort,
  options,
  pagination,
  setFilterOpen,
  sort,
  takerMarket,
}: {
  clearFilter: () => void;
  handleSort: (key: IneligibleInvoiceSortKey) => void;
  options: Partial<IneligibleInvoiceFetchOptions>;
  pagination: {
    currentPage: number;
    onNextChange: () => void;
    onPageSizeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    onPrevChange: () => void;
    pageSize: number;
  };
  setFilterOpen: (open: boolean) => void;
  sort: IneligibleInvoiceTableSort;
  takerMarket: TakerMarket;
}) => {
  const { t } = useTranslation();
  const { asCurrency, asDateStringText } = useLocaleFormat();
  const hasIndiaDivisions = useUserHasIndiaDivisions();
  const {
    urlParams: { marketId, takerId },
  } = useBuyerNavigationLayoutContext();
  const {
    data: ineligibleInvoices,
    isFetching,
    isPending,
    isSuccess,
  } = useIneligibleInvoices({
    marketId,
    takerId,
    options,
  });

  // We've already got the first set, but now we are getting an additional page.
  const isFetchingAdditionalInvoices = isSuccess && isFetching;

  const { data: ineligibleInvoiceStats } = useIneligibleInvoiceStats({
    marketId,
    takerId,
    options,
  });
  const invoiceDownloadUrl = useInvoiceDownloadUrl({ filters: options?.filter, marketId, takerId, type: 'ineligible' });
  const appliedFilters = Object.keys(options.filter ?? {});

  return (
    <>
      {/* ACTIONS */}
      <div className="flex items-center justify-between gap-6 border-b border-stroke bg-canvas px-4 py-6">
        <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center lg:gap-8">
          <div className="flex flex-wrap items-center gap-1">
            <div className="font-medium">{t('core.ineligibleAmount')}:</div>
            <div>{asCurrency(ineligibleInvoiceStats?.subTotal ?? 0, takerMarket.currency)}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Button onClick={() => setFilterOpen(true)} variant="primary">
                {t('core.filters')}
              </Button>
              {appliedFilters.length > 0 && (
                <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-lightBlue-500 text-xs font-medium text-white">
                  {appliedFilters.length}
                </div>
              )}
            </div>
            <Button disabled={appliedFilters.length < 1} onClick={clearFilter} variant="secondary">
              {t('core.clearFilters')}
            </Button>
          </div>
        </div>
        <div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton icon={EllipsisVerticalIcon} name="dropdown" variant="primary" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" avoidCollisions={false} className="w-44" collisionPadding={24}>
              <DropdownMenuItem
                asChild
                disabled={(ineligibleInvoiceStats?.count ?? 0) === 0 || (ineligibleInvoices?.length ?? 0) === 0}
              >
                <a href={invoiceDownloadUrl} target="_self">
                  {t('core.download')}
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {/* TABLE */}
      <div className="w-full overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead isSorted={sort.key === 'makerExcluded'} onClick={() => handleSort('makerExcluded')}>
                <TableSortArrow accessorKey="makerExcluded" sort={sort}>
                  {t('core.status')}
                </TableSortArrow>
              </TableHead>
              <TableHead
                className="max-w-[224px]"
                isSorted={sort.key === 'voucherId'}
                onClick={() => handleSort('voucherId')}
              >
                <TableSortArrow accessorKey="voucherId" sort={sort}>
                  {t('core.invoiceId')}
                </TableSortArrow>
              </TableHead>
              <TableHead isSorted={sort.key === 'dueDate'} onClick={() => handleSort('dueDate')}>
                <TableSortArrow accessorKey="dueDate" sort={sort} textRight>
                  {hasIndiaDivisions ? t('core.tentativePaymentDate') : t('core.originalDueDate')}
                </TableSortArrow>
              </TableHead>
              <TableHead isSorted={sort.key === 'originalAmount'} onClick={() => handleSort('originalAmount')}>
                <TableSortArrow accessorKey="originalAmount" sort={sort} textRight>
                  {t('core.amount')}
                </TableSortArrow>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody loading={isFetchingAdditionalInvoices}>
            {isSuccess && ineligibleInvoices.length > 0 ? (
              ineligibleInvoices.map((ineligibleInvoice) => (
                <TableRow key={ineligibleInvoice.id}>
                  <TableCell>
                    <span className={getInvoiceDisplay(ineligibleInvoice).color}>
                      {t(getInvoiceDisplay(ineligibleInvoice).translation)}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-[224px] whitespace-pre-wrap break-words">
                    {ineligibleInvoice.voucherId}
                  </TableCell>
                  <TableCell className="text-right">
                    {ineligibleInvoice.dueDate && asDateStringText({ date: ineligibleInvoice.dueDate })}
                  </TableCell>
                  <TableCell className="text-right">
                    {asCurrency(ineligibleInvoice.originalAmount, ineligibleInvoice.currency)}
                  </TableCell>
                </TableRow>
              ))
            ) : isPending ? (
              <TableDisplaySkeletonRows colSpan={4} rowCount={options.page?.limit} />
            ) : (
              <TableDisplay colSpan={4}>
                <NoDataState
                  className="py-8"
                  {...(options.filter
                    ? {
                        icon: <img src={emptystatements} alt="emptystatements" className="h-28 w-28" />,
                        title: t('invoices.noMatchingInvoices'),
                        message: t('invoices.noMatchingEligibleInvoicesDescription'),
                      }
                    : {
                        icon: <img src={invoices} alt="invoices" className="h-28 w-28" />,
                        title: t('invoices.noAvailableInvoices'),
                        message: t('invoices.noAvailableIneligibleInvoicesDescription'),
                      })}
                />
              </TableDisplay>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        className="mt-auto"
        disableNext={
          pagination.currentPage ===
          Math.ceil((ineligibleInvoiceStats?.count ?? pagination.pageSize) / pagination.pageSize)
        }
        disablePrev={pagination.currentPage === 1}
        onNextChange={() => pagination.onNextChange()}
        onPageSizeChange={(e) => pagination.onPageSizeChange(e)}
        onPrevChange={() => pagination.onPrevChange()}
        pageSize={pagination.pageSize}
      />
    </>
  );
};

export default IneligibleInvoiceTable;
